
/**
 * Core Analytics Engine for calculating performance metrics from test results.
 * Used for both real-time results display and long-term profile tracking.
 */

export interface SubjectStat {
    subject: string;
    accuracy: number;
    attemptRate: number;
    avgTime: number;
    total: number;
    correct: number;
}

export interface StruggleZone {
    name: string;
    subject: string;
    accuracy: number;
    userAvgTime: number;
    communalAvg: number;
}

export interface AnalyticsResult {
    subjects: SubjectStat[];
    struggleZones: StruggleZone[];
}

export const calculateAnalytics = (questions: any[], results: any): AnalyticsResult => {
    const subjectStats: { [key: string]: { correct: number; total: number; attempted: number; time: number } } = {};
    const chapterStats: { [key: string]: { correct: number; total: number; attempted: number; time: number; communalAvg: number; subject: string } } = {};
    
    questions.forEach((question, index) => {
        const subject = question.subject || question.Subject || 'General';
        const chapter = question.topic || question.tags || question.chapter || question.Chapter || 'General';
        const userTime = results.questionTimes?.[index] || results.questionTimes?.[index.toString()] || 0;
        
        let communalAvg = 0;
        try {
            const communalStats = typeof question.avg_time === 'string' ? JSON.parse(question.avg_time) : (question.avg_time || { avg: 0 });
            communalAvg = communalStats.avg || 0;
        } catch (e) {
            communalAvg = 0;
        }

        // Subject aggregate
        if (!subjectStats[subject]) {
            subjectStats[subject] = { correct: 0, total: 0, attempted: 0, time: 0 };
        }
        subjectStats[subject].total++;
        subjectStats[subject].time += userTime;

        // Chapter aggregate
        if (!chapterStats[chapter]) {
            chapterStats[chapter] = { correct: 0, total: 0, attempted: 0, time: 0, communalAvg: 0, subject: subject };
        }
        chapterStats[chapter].total++;
        chapterStats[chapter].time += userTime;
        chapterStats[chapter].communalAvg = (chapterStats[chapter].communalAvg + (communalAvg || 45)) / 2;

        const userAnswer = results.answers?.[index] || results.answers?.[index.toString()];
        if (userAnswer !== undefined && userAnswer !== null && userAnswer !== '') {
            subjectStats[subject].attempted++;
            chapterStats[chapter].attempted++;
            
            const isCorrect = question.type === 'Integer'
                ? userAnswer.trim() === (question.answer?.toString() || '')
                : userAnswer.trim().toLowerCase() === (question.answer?.toString() || '').toLowerCase();

            if (isCorrect) {
                subjectStats[subject].correct++;
                chapterStats[chapter].correct++;
            }
        }
    });

    const struggleZones = Object.entries(chapterStats)
        .map(([name, stats]) => ({
            name,
            subject: stats.subject,
            accuracy: stats.attempted > 0 ? (stats.correct / stats.attempted) * 100 : 0,
            userAvgTime: stats.attempted > 0 ? stats.time / stats.attempted : 0,
            communalAvg: stats.communalAvg
        }))
        .filter(c => c.accuracy < 70 || c.userAvgTime > c.communalAvg * 1.5)
        .sort((a, b) => a.accuracy - b.accuracy);

    return {
        subjects: Object.entries(subjectStats).map(([subject, stats]) => ({
            subject,
            accuracy: stats.attempted > 0 ? (stats.correct / stats.attempted) * 100 : 0,
            attemptRate: (stats.attempted / stats.total) * 100,
            avgTime: stats.attempted > 0 ? stats.time / stats.attempted : 0,
            total: stats.total,
            correct: stats.correct
        })),
        struggleZones: struggleZones.slice(0, 5)
    };
};

/**
 * Merges a new test snapshot into the long-term performance profile.
 */
export const updatePerformanceProfile = (currentProfile: any = {}, newSnapshot: AnalyticsResult) => {
    const profile = { 
        subjects: { ...(currentProfile.subjects || {}) },
        struggleZones: [...(currentProfile.struggleZones || [])],
        lastUpdated: new Date().toISOString(),
        totalTests: (currentProfile.totalTests || 0) + 1
    };

    // Update subjects (Moving average / Cumulative totals)
    newSnapshot.subjects.forEach(stat => {
        const existing = profile.subjects[stat.subject] || { total: 0, correct: 0, time: 0, attempts: 0 };
        profile.subjects[stat.subject] = {
            total: existing.total + stat.total,
            correct: existing.correct + stat.correct,
            attempts: existing.attempts + (stat.total * (stat.attemptRate / 100)),
            time: existing.time + (stat.avgTime * (stat.total * (stat.attemptRate / 100))), // total time spent
            accuracy: ((existing.correct + stat.correct) / (existing.total + stat.total)) * 100, // This is simple accuracy
            // More refined: weighted by volume
        };
        
        // Recalculate accuracy based on attempts
        const totalAttempts = profile.subjects[stat.subject].attempts;
        profile.subjects[stat.subject].accuracy = totalAttempts > 0 
            ? (profile.subjects[stat.subject].correct / totalAttempts) * 100 
            : 0;
        
        profile.subjects[stat.subject].avgTime = totalAttempts > 0
            ? profile.subjects[stat.subject].time / totalAttempts
            : 0;
    });

    // Update Struggle Zones (Persistent weaknesses)
    // We merge new ones and keep the ones that are still relevant
    const newZones = newSnapshot.struggleZones.map(z => ({ ...z, lastSeen: new Date().toISOString() }));
    
    // Simple strategy: Replace old struggle zones with new ones, but maybe keep some history?
    // Filtering old zones that weren't in this test but were high priority?
    // For now, let's just take the union and sort by worst accuracy.
    const allZones = [...newZones];
    
    // Add existing ones if they aren't already represented (unique by name)
    profile.struggleZones.forEach(old => {
        if (!allZones.find(z => z.name === old.name)) {
            allZones.push(old);
        }
    });

    profile.struggleZones = allZones
        .sort((a, b) => a.accuracy - b.accuracy)
        .slice(0, 10); // Keep top 10 most challenging topics

    return profile;
};
